import { makeAutoObservable, runInAction } from 'mobx'

import { HoldingApi } from '../api'
import { AnalysisType } from '../constants/Investments'
import { type Holding } from '../types'
import {
  analysisChartData,
  analysisDetailsData,
  holdingHasAllocationData,
  holdingHasStyleData,
  mapDataToPercentageGrid,
} from '../utils/InvestmentUtil'

import { GlobalStore } from './GlobalStore'

export class HoldingStore {
  analysisType: AnalysisType = AnalysisType.Stocks
  api = new HoldingApi('/', '')
  holdings: Holding[] = []
  holdingsLoaded = false
  globalStore: GlobalStore
  showConnectionsWidget = false

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore
    this.api = new HoldingApi(globalStore.endpoint, globalStore.sessionToken, globalStore.onError)

    makeAutoObservable(this)
  }

  get analysisChartData() {
    return mapDataToPercentageGrid(analysisChartData(this.visibleHoldings), this.analysisType)
  }

  get analysisDetails() {
    return analysisDetailsData(this.visibleHoldings)[this.analysisType] || {}
  }

  get visibleHoldings() {
    return this.holdings.filter((holding) =>
      this.globalStore.globalUiStore.selectedAccountGuids.includes(holding.account_guid),
    )
  }

  // Cash counts because a cash balance renders as a synthetic donut slice.
  get hasAllocationData() {
    if (this.visibleHoldings.some(holdingHasAllocationData)) return true

    return this.selectedInvestmentAccounts.some((account) => (account.cash_balance ?? 0) > 0)
  }

  get hasAnalysisData() {
    return this.visibleHoldings.some(holdingHasStyleData)
  }

  get selectedInvestmentAccounts() {
    const { selectedAccountGuids } = this.globalStore.globalUiStore
    return this.globalStore.accountStore.visibleInvestmentAccounts.filter((account) =>
      selectedAccountGuids.includes(account.guid),
    )
  }

  setHoldings = (holdings: Holding[]) => {
    this.holdings = holdings
    this.holdingsLoaded = true
  }

  setShowConnectionsWidget = (value: boolean) => {
    this.showConnectionsWidget = value
  }

  loadHoldings = async () => {
    try {
      const holdings = await this.api.getHoldings()
      this.setHoldings(holdings)
    } catch (error) {
      console.error('Error occurred while getting holdings:', error)
      this.globalStore.captureException(error, { store: 'HoldingStore' })
    }
  }

  setAnalysisType = (analysisType: AnalysisType) => {
    this.analysisType = analysisType
  }

  updateHoldings = async (holding: Holding) => {
    this.holdingsLoaded = false
    try {
      const updatedHolding = await this.api.updateHoldings(holding)
      const updatedIndex = this.holdings.findIndex(({ guid }) => guid === holding.guid)

      if (updatedIndex >= 0) {
        const holdings = [...this.holdings]
        holdings[updatedIndex] = updatedHolding

        runInAction(() => {
          this.holdings = holdings
          this.holdingsLoaded = true
        })
      }
    } catch (error) {
      console.error('Error occurred while updating holding:', error)
      this.globalStore.captureException(error, { store: 'HoldingStore' })
    }
  }
}
